import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  QrCode,
  Printer,
  ChevronLeft,
  Search,
  Check,
  Package,
  Loader2,
  Download,
  X
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PrintQRCodes = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/inventory`);
      setItems(response.data);
    } catch (error) {
      toast.error("Failed to load inventory");
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = items.filter(item =>
    item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.qr_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.location?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleSelection = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const toggleAll = () => {
    if (selectedIds.size === filteredItems.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredItems.map(item => item.id)));
    }
  };

  const handleExportPDF = async () => {
    if (selectedIds.size === 0) {
      toast.error("Select at least one item to export");
      return;
    }
    
    if (selectedIds.size > 100) {
      toast.error("Maximum 100 items per export");
      return;
    }

    setExporting(true);
    
    try {
      const response = await axios.post(
        `${API}/inventory/qr-batch/pdf`,
        { item_ids: Array.from(selectedIds) },
        { responseType: 'blob' }
      );
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `qr_labels_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${selectedIds.size} QR codes to PDF`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export QR codes");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="print-qr-codes-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/inventory")}
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-semibold flex items-center gap-3">
              <QrCode className="h-8 w-8 text-indigo-600" />
              Print QR Labels
            </h1>
            <p className="text-muted-foreground mt-1">
              Select items to export as printable QR code labels
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {selectedIds.size > 0 && (
            <Badge variant="secondary" className="text-sm px-3 py-1">
              {selectedIds.size} selected
            </Badge>
          )}
          <Button
            onClick={handleExportPDF}
            disabled={selectedIds.size === 0 || exporting}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="export-qr-pdf-btn"
          >
            {exporting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Printer className="h-4 w-4 mr-2" />
            )}
            Export PDF ({selectedIds.size})
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search items by name, QR code, or location..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="qr-search-input"
        />
      </div>

      {/* Selection Actions */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={toggleAll}
          data-testid="select-all-btn"
        >
          {selectedIds.size === filteredItems.length ? (
            <>
              <X className="h-4 w-4 mr-2" /> Deselect All
            </>
          ) : (
            <>
              <Check className="h-4 w-4 mr-2" /> Select All ({filteredItems.length})
            </>
          )}
        </Button>
        
        {selectedIds.size > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedIds(new Set())}
          >
            Clear Selection
          </Button>
        )}
      </div>

      {/* Items Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      ) : filteredItems.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Package className="h-12 w-12 text-muted-foreground mb-4" strokeWidth={1} />
            <p className="font-medium">No items found</p>
            <p className="text-sm text-muted-foreground mt-1">
              Try adjusting your search
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={selectedIds.size === filteredItems.length && filteredItems.length > 0}
                    onCheckedChange={toggleAll}
                    data-testid="select-all-checkbox"
                  />
                </TableHead>
                <TableHead>Item Name</TableHead>
                <TableHead>QR Code</TableHead>
                <TableHead>Location</TableHead>
                <TableHead className="text-center">Qty</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.map((item) => (
                <TableRow 
                  key={item.id}
                  className={selectedIds.has(item.id) ? "bg-indigo-50" : ""}
                  onClick={() => toggleSelection(item.id)}
                  style={{ cursor: "pointer" }}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedIds.has(item.id)}
                      onCheckedChange={() => toggleSelection(item.id)}
                      data-testid={`select-item-${item.id}`}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell>
                    <code className="text-xs bg-stone-100 px-2 py-1 rounded">
                      {item.qr_code}
                    </code>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {item.location}
                    {item.sub_location && ` - ${item.sub_location}`}
                  </TableCell>
                  <TableCell className="text-center">{item.quantity}</TableCell>
                  <TableCell>
                    <Badge
                      className={
                        item.status === "available" ? "bg-green-100 text-green-700" :
                        item.status === "hold" ? "bg-purple-100 text-purple-700" :
                        item.status === "low_stock" ? "bg-red-100 text-red-700" :
                        "bg-gray-100 text-gray-700"
                      }
                    >
                      {item.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Print Preview Info */}
      {selectedIds.size > 0 && (
        <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-indigo-800">Ready to Print</p>
                <p className="text-sm text-indigo-600">
                  {selectedIds.size} QR labels will be generated ({Math.ceil(selectedIds.size / 21)} page{Math.ceil(selectedIds.size / 21) > 1 ? "s" : ""})
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Layout: 3 columns × 7 rows per page
                </p>
              </div>
              <QrCode className="h-12 w-12 text-indigo-300" />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
